import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { linkTo } from "../../i18n/lang.mjs";
import { WIGS_ENABLED } from "../../config/features.mjs";

const SOCIALS = [
  { href: "https://www.facebook.com/js.makeup.35/",    icon: "fab fa-facebook-f", title: "Facebook"  },
  { href: "https://www.instagram.com/visionbeauty.rs/", icon: "fab fa-instagram",  title: "Instagram" },
];

const Heading = ({ children }) => (
  <span style={{
    display: "block",
    fontFamily: "'Inter', sans-serif",
    fontSize: "10px",
    fontWeight: "700",
    letterSpacing: "0.24em",
    textTransform: "uppercase",
    color: "#D4A96A",
    marginBottom: "20px",
  }}>
    {children}
    <span style={{
      display: "block",
      width: "20px",
      height: "1px",
      background: "rgba(212,169,106,0.45)",
      marginTop: "10px",
    }} />
  </span>
);

const Main = () => {
  const { t, i18n } = useTranslation();
  const lang = ["sr", "en", "de"].includes(i18n.language) ? i18n.language : "sr";

  const navLinks = [
    { to: "/",            label: t('footer.links.home')       },
    { to: "/service",     label: t('footer.links.services')   },
    { to: "/pricing",     label: t('footer.links.pricing')    },
    { to: "/educations",  label: t('footer.links.educations') },
    // Translated slug per language - see ROUTE_SLUGS in src/i18n/lang.mjs.
    { to: linkTo("/napredni-kurs-sminkanja", lang), label: t('footer.links.advancedCourse') },
    { to: linkTo("/ponude", lang), label: t('footer.links.offers') },
    { to: "/gallery",     label: t('footer.links.gallery')    },
    { to: "/blog",        label: t('footer.links.blog')       },
    { to: "/contact",     label: t('footer.links.contact')    },
  ];

  const serviceLinks = [
    { to: "/permanent-makeup", label: t('footer.services.permanentMakeup') },
    { to: "/vega",             label: t('footer.services.vega')            },
    { to: "/baby-lips",        label: t('footer.services.babyLips')        },
    { to: "/eyelashes",        label: t('footer.services.eyelashes')       },
    { to: "/nails",            label: t('footer.services.nails')           },
    { to: "/depilation",       label: t('footer.services.depilation')      },
    { to: "/hairdresser",      label: t('footer.services.hairdresser')     },
    { to: linkTo("/farbanje-kose", lang),      label: t('footer.services.colouring')   },
    { to: "/balayage",                         label: t('footer.services.balayage')    },
    { to: linkTo("/tretmani-kose", lang),      label: t('footer.services.treatments')  },
    { to: linkTo("/musko-sisanje", lang),      label: t('footer.services.mensHaircut') },
    { to: linkTo("/ekstenzije-za-kosu", lang), label: t('footer.services.extensions')  },
    // Wigs are not in stock yet - see src/config/features.mjs.
    ...(WIGS_ENABLED ? [{ to: linkTo("/perike", lang), label: t('footer.services.wigs') }] : []),
  ];

  const taglineLines = t('footer.tagline').split('\n');

  return (
    <>
      <style>{`
        .vb-fl {
          display: block;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          font-weight: 400;
          color: rgba(255,255,255,0.48);
          text-decoration: none;
          letter-spacing: 0.02em;
          padding: 4px 0;
          transition: color 0.22s ease, transform 0.22s ease;
        }
        .vb-fl:hover { color: #D4A96A !important; transform: translateX(4px); }

        .vb-cl {
          display: flex;
          align-items: center;
          gap: 11px;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: rgba(255,255,255,0.48);
          text-decoration: none;
          letter-spacing: 0.02em;
          padding: 4px 0;
          transition: color 0.22s ease;
        }
        .vb-cl:hover { color: #D4A96A !important; }

        .vb-sb {
          width: 38px; height: 38px; border-radius: 50%;
          border: 1px solid rgba(255,255,255,0.13);
          display: flex; align-items: center; justify-content: center;
          color: rgba(255,255,255,0.45); font-size: 13px;
          text-decoration: none;
          transition: background 0.24s ease, border-color 0.24s ease, color 0.24s ease;
        }
        .vb-sb:hover {
          background: rgba(212,169,106,0.13) !important;
          border-color: rgba(212,169,106,0.48) !important;
          color: #D4A96A !important;
        }

        .vb-appt {
          display: inline-block;
          font-family: 'Inter', sans-serif;
          font-size: 11px; font-weight: 700;
          letter-spacing: 0.15em; text-transform: uppercase;
          color: #D4A96A; text-decoration: none;
          padding: 10px 24px;
          border: 1px solid rgba(212,169,106,0.32);
          border-radius: 9999px;
          transition: background 0.24s ease, border-color 0.24s ease;
          margin-top: 22px;
        }
        .vb-appt:hover {
          background: rgba(212,169,106,0.11) !important;
          border-color: rgba(212,169,106,0.58) !important;
        }

        @media (max-width: 767px) {
          .vb-ft-col + .vb-ft-col {
            border-top: 1px solid rgba(255,255,255,0.06);
            padding-top: 28px;
            margin-top: 4px;
          }
        }
      `}</style>

      <footer style={{
        background: "linear-gradient(155deg, #1c0f06 0%, #0d0703 100%)",
        borderTop: "1px solid rgba(212,169,106,0.20)",
      }}>

        {/* ── MAIN GRID ── */}
        <div className="container" style={{ maxWidth: "1280px", paddingTop: "56px", paddingBottom: "44px" }}>
          <div className="row g-0">

            {/* Col 1 - Brand + Social */}
            <div className="col-12 col-md-6 col-lg-3 vb-ft-col" style={{ paddingRight: "32px", paddingBottom: "32px" }}>
              <Heading>Vision Beauty</Heading>

              <p style={{
                fontFamily: "'Cormorant Garamond', 'PT Serif', serif",
                fontSize: "15px",
                fontStyle: "italic",
                color: "rgba(255,255,255,0.32)",
                lineHeight: "1.75",
                margin: "0 0 24px",
                maxWidth: "190px",
              }}>
                {taglineLines[0]}<br />{taglineLines[1]}
              </p>

              <div style={{ display: "flex", gap: "10px" }}>
                {SOCIALS.map(({ href, icon, title }) => (
                  <a key={title} href={href} title={title} className="vb-sb">
                    <i className={icon} />
                  </a>
                ))}
              </div>
            </div>

            {/* Col 2 - Navigation */}
            <div className="col-12 col-md-6 col-lg-2 vb-ft-col" style={{ paddingBottom: "32px", paddingLeft: "0", paddingRight: "16px" }}>
              <Heading>{t('footer.navHeading')}</Heading>
              <nav>
                {navLinks.map(({ to, label }) => (
                  <Link key={to} to={to} className="vb-fl">{label}</Link>
                ))}
              </nav>
            </div>

            {/* Col 3 - Services */}
            <div className="col-12 col-md-6 col-lg-3 vb-ft-col" style={{ paddingBottom: "32px", paddingLeft: "0", paddingRight: "16px" }}>
              <Heading>{t('footer.servicesHeading')}</Heading>
              <nav>
                {serviceLinks.map(({ to, label }) => (
                  <Link key={to} to={to} className="vb-fl">{label}</Link>
                ))}
              </nav>
            </div>

            {/* Col 4 - Contact */}
            <div className="col-12 col-md-6 col-lg-4 vb-ft-col" style={{ paddingBottom: "32px", paddingLeft: "0" }}>
              <Heading>{t('footer.contactHeading')}</Heading>

              {/* Full NAP inside <address> for local SEO. Street stays literally
                  "Miljkovačka 1" in every language to match the Google Business profile. */}
              <address style={{ fontStyle: "normal", display: "flex", flexDirection: "column", gap: "10px" }}>
                <span className="vb-cl" style={{ cursor: "default", alignItems: "flex-start" }}>
                  <i className="fas fa-map-marker-alt" style={{ color: "#D4A96A", fontSize: "13px", width: "14px", flexShrink: 0, marginTop: "3px" }} />
                  Miljkovačka 1, 18000 Niš
                </span>
                <a href="tel:+381653585545" className="vb-cl">
                  <i className="fas fa-phone-alt" style={{ color: "#D4A96A", fontSize: "12px", width: "14px", flexShrink: 0 }} />
                  065/3585-545
                </a>
                <a href="mailto:visionbeauty25@gmail.com" className="vb-cl">
                  <i className="far fa-envelope" style={{ color: "#D4A96A", fontSize: "13px", width: "14px", flexShrink: 0 }} />
                  visionbeauty25@gmail.com
                </a>
                <span className="vb-cl" style={{ cursor: "default", alignItems: "flex-start" }}>
                  <i className="far fa-clock" style={{ color: "#D4A96A", fontSize: "13px", width: "14px", flexShrink: 0, marginTop: "3px" }} />
                  {t('footer.hours')}
                </span>
              </address>

              <Link to="/contact" className="vb-appt">{t('footer.bookAppointment')}</Link>
            </div>

          </div>
        </div>

        {/* ── COPYRIGHT BAR ── */}
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)", padding: "18px 0" }}>
          <div className="container" style={{ maxWidth: "1280px" }}>
            <p style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "11px",
              letterSpacing: "0.06em",
              color: "rgba(255,255,255,0.22)",
              margin: 0,
              textAlign: "center",
            }}>
              {t('footer.copyright')}
            </p>
            <p style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "10px",
              letterSpacing: "0.06em",
              color: "rgba(255,255,255,0.16)",
              margin: "6px 0 0",
              textAlign: "center",
            }}>
              {t('footer.taxInfo')}
            </p>
            <p style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "10px",
              letterSpacing: "0.06em",
              color: "rgba(255,255,255,0.16)",
              margin: "6px 0 0",
              textAlign: "center",
            }}>
              {t('footer.ratesBy')}{' '}
              <a
                href="https://www.exchangerate-api.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "inherit", textDecoration: "underline" }}
              >
                exchangerate-api.com
              </a>
            </p>
          </div>
        </div>

      </footer>
    </>
  );
};

export default Main;
